import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { FC } from "react";

/**
 * Represents a dashboard widget descriptor.
 * This is a purely client-side UI concept — the widget objects are constructed
 * in dashboardWidgetMapper.tsx and are never fetched directly from the backend.
 */
export interface DashboardWidget {
  /** React component rendered as the widget tile */
  component: FC;
  title?: string;
  /** Navigation path for the widget share/navigate action */
  path?: string;
  propertiesOption?: Array<{ id: string; text: string }>;
  /** Whether the widget supports expand/maximize mode */
  expand?: boolean;
  isAdded?: boolean;
  allowIn?: string[];
  layout?: { w: number; h: number; x: number; y: number };
  previewImg?: string;
  module?: string;
  /** Arbitrary additional widget data (e.g. pre-loaded row data, config) */
  data?: Record<string, string | number | boolean | null | undefined>;
}

export interface ProjectDashboardState {
  isModalVisible: boolean;
  title: string;
  /** The React component to render inside the modal */
  component: FC | null;
  patchForProject: boolean;
  /** Additional widget data passed to the modal */
  widget?: DashboardWidget;
  widgetName?: string;
}

const initialProjectDashboardDataState: ProjectDashboardState = {
  isModalVisible: false,
  title: "",
  component: null,
  patchForProject: false,
};

const projectDashboardSlice = createSlice({
  name: "projectDashboard",
  initialState: initialProjectDashboardDataState,
  reducers: {
    setProjectModalVisiblity: (state: ProjectDashboardState, action: PayloadAction<Partial<ProjectDashboardState>>) => {
      Object.assign(state, action.payload);
    },
    setPatchForProject: (state: ProjectDashboardState, action: PayloadAction<boolean>) => {
      state.patchForProject = action.payload;
    },
  },
});

export const { setProjectModalVisiblity, setPatchForProject } = projectDashboardSlice.actions;

export const projectDashboardData = projectDashboardSlice.reducer;

export default projectDashboardSlice.reducer;
